// Port of load_chase_csv in scripts/loaders.py.
// Chase native convention: negative = debit/charge, positive = payment/deposit.
// Flip to project's positive=expense convention.
// Account label inferred from header columns:
//   "Category" or "Memo" present → "Chase Card"
//   otherwise                    → "Chase Checking"

import {
  type Transaction,
  newTransaction,
  makeId,
  parseDate,
  parseAmount,
  normalizeVendor,
  isInterPortalTransfer,
} from "../common.js";
import { parseCsv, toDictRows, findHeaderIndex } from "../csv.js";

export async function loadChase(
  text: string,
  filename: string,
): Promise<Transaction[]> {
  const rows = parseCsv(text);
  const headerIdx = findHeaderIndex(rows, ["Description", "Amount"]);
  if (headerIdx < 0) return [];
  const header = rows[headerIdx].map((h) => (h || "").trim());
  const dictRows = toDictRows(header, rows.slice(headerIdx + 1));

  const isCard = header.includes("Category") || header.includes("Memo");
  const accountLabel = isCard ? "Chase Card" : "Chase Checking";
  // Chase card = liability. Chase checking = debit. Same shape of CSV
  // for both; the "Category"/"Memo" column presence is what flags credit.
  const accountKind: "liability" | "debit" = isCard ? "liability" : "debit";

  const out: Transaction[] = [];
  for (const row of dictRows) {
    const rawDate = (
      row["Transaction Date"] ||
      row["Posting Date"] ||
      row["Post Date"] ||
      ""
    ).trim();
    if (!rawDate) continue;
    let dateStr: string;
    try {
      dateStr = parseDate(rawDate);
    } catch {
      continue;
    }
    const amount = -parseAmount(row["Amount"] || "0");
    if (amount === 0) continue;

    const description = (row["Description"] || "").trim().replace(/^"|"$/g, "");
    if (isInterPortalTransfer(description)) continue;

    const id = await makeId("chase", accountLabel, dateStr, amount, description);
    out.push(
      newTransaction({
        id,
        date: dateStr,
        vendor_raw: description,
        vendor_key: normalizeVendor(description) || "chase",
        amount: Math.round(amount * 100) / 100,
        portal: "chase",
        account: accountLabel,
        account_kind: accountKind,
        source_file: filename,
        raw: { ...row },
      }),
    );
  }
  return out;
}
